import type { FC, PropsWithChildren } from 'react'

import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import * as CopyText from '@segment/matcha/recipes/CopyText'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import useMatchMedia from '@segment/utils/useMatchMedia'

import heroLgPng2x from './assets/Hero-lg@2x.png'
import heroLgWebp from './assets/Hero-lg.webp'
import heroLgWebp2x from './assets/Hero-lg@2x.webp'
import heroLgWebp3x from './assets/Hero-lg@3x.webp'

import heroXsPng2x from './assets/Hero-xs@2x.png'
import heroXsWebp from './assets/Hero-xs.webp'
import heroXsWebp2x from './assets/Hero-xs@2x.webp'
import heroXsWebp3x from './assets/Hero-xs@3x.webp'

const copy = {
  overline: `Segment’s Stance on Security `,
  heading: `Secure and private by default`,
  description: `We take the responsibility of helping you manage your customer data seriously. That’s why security and privacy are key focus areas for our organization and product development.`,
}

const Hero: FC<PropsWithChildren<unknown>> = () => {
  const theme = useTheme()
  const desktopResolutionMediaQuery = theme.breakpoints.up(`lg`, false)
  const isDesktop = useMatchMedia(desktopResolutionMediaQuery, true)
  let { webp, webp2, webp3, png2, size } = {
    webp: heroXsWebp,
    webp2: heroXsWebp2x,
    webp3: heroXsWebp3x,
    png2: heroXsPng2x,
    size: [312, 469],
  }

  if (isDesktop) {
    webp = heroLgWebp
    webp2 = heroLgWebp2x
    webp3 = heroLgWebp3x
    png2 = heroLgPng2x
    size = [606, 402]
  }

  return (
    <Wrapper>
      <StyledHeroBase variant="2Col">
        <HeroRecipeCopy>
          <CopyText.Overline variant="h5" tag="h5">
            {copy.overline}
          </CopyText.Overline>
          <CopyText.Heading variant="h1" tag="h1">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="pExtraLarge" tag="h2">
            {copy.description}
          </CopyText.Description>
        </HeroRecipeCopy>
        <HeroRecipe.Contents>
          <picture>
            <source type="image/webp" srcSet={`${webp}, ${webp2} 2x, ${webp3} 3x`} />
            <HeroRecipe.Image width={size[0]} height={size[1]} src={png2} alt="Hero" loading="lazy" />
          </picture>
        </HeroRecipe.Contents>
      </StyledHeroBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background-color: ${({ theme }) => theme.palette.twilio.green[10]};
`

const StyledHeroBase = styled(HeroBase)`
  ${HeroRecipe.Wrapper} {
    ${({ theme }) => theme.breakpoints.down(`md`)} {
      text-align: left;
    }
  }
`

const HeroRecipeCopy = styled(HeroRecipe.Copy)`
  align-self: flex-start;
`

export default Hero
